import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/fetchWithAuth';
import { UserProfile } from '@shared/types';

interface Organization {
  id: string;
  name: string;
  code: string;
  logo?: string;
  type: 'organization' | 'region' | 'location';
}

interface OrganizationContextType {
  currentOrganizationId: string | null;
  currentOrganization: Organization | null;
  organizations: Organization[];
  setOrganizationId: (orgId: string) => void;
  isSwitching: boolean;
  isLoading: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};

interface OrganizationProviderProps {
  children: ReactNode;
  user: UserProfile | null;
}

export function OrganizationProvider({ children, user }: OrganizationProviderProps) {
  const queryClient = useQueryClient();
  
  // Get initial organization from user profile (no localStorage)
  const getInitialOrgId = () => {
    return user?.organizationId || user?.organizationIds?.[0] || null;
  };
  
  const [currentOrganizationId, setCurrentOrganizationId] = useState<string | null>(getInitialOrgId());
  const [isSwitching, setIsSwitching] = useState(false);
  const switchingRef = useRef(false);

  // Fetch organizations the user has access to
  const { data: organizations = [], isLoading } = useQuery<Organization[]>({
    queryKey: ['user-organizations'],
    queryFn: async () => {
      const response = await apiRequest<Organization[]>('/api/organizations/user-access');
      return response || [];
    },
    enabled: !!user,
  });

  // Find current organization
  const currentOrganization = organizations.find(org => org.id === currentOrganizationId) || null;

  // Enhanced organization switching with debouncing and loading states
  const setOrganizationId = useCallback((newId: string) => {
    if (switchingRef.current || newId === currentOrganizationId) return;
    
    console.log('🔄 Organization switch initiated:', { from: currentOrganizationId, to: newId });
    
    switchingRef.current = true;
    setIsSwitching(true);
    setCurrentOrganizationId(newId);
    
    // Debounced cache clear with specific invalidations
    setTimeout(() => {
      // Invalidate queries with organizationId
      queryClient.invalidateQueries({
        queryKey: ['locations', newId],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ['users', newId],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ['boards', newId],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ['headlines', newId],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ['enhanced-headlines', newId],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ['meetings', newId],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ['tasks', newId],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ['nps', newId],
        exact: false
      });
      // Invalidate NPS API queries
      queryClient.invalidateQueries({
        queryKey: ['/api/nps/summary'],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ['/api/nps/patient-calls'],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ['/api/google-reviews'],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ['employees', newId],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ['dashboard', newId],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ['dashboard-metrics'],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ['metrics'],
        exact: false
      });
      
      // Invalidate location-based queries
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const queryKey = query.queryKey[0] as string;
          return queryKey === 'locations';
        }
      });
      
      // Invalidate user queries
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const queryKey = query.queryKey[0] as string;
          return queryKey?.includes('/api/users');
        }
      });
      
      // Invalidate NPS queries to force fresh data for new organization
      queryClient.invalidateQueries({
        queryKey: ['/api/nps/summary'],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ['/api/nps/patient-calls'],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ['/api/google-reviews'],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ['locations'],
        exact: false
      });
      
      switchingRef.current = false;
      setIsSwitching(false);
      console.log('✅ Organization switch completed');
    }, 300); // 300ms debounce
  }, [currentOrganizationId, queryClient]);

  // Set initial organization if not set
  useEffect(() => {
    if (!currentOrganizationId && organizations.length > 0) {
      setOrganizationId(organizations[0].id);
    }
  }, [organizations, currentOrganizationId, setOrganizationId]);

  const value: OrganizationContextType = {
    currentOrganizationId,
    currentOrganization,
    organizations,
    setOrganizationId,
    isSwitching,
    isLoading,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}
